import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import PageState from '@/shared/components/PageState';
import ResultLoadingState from '@/shared/components/ResultLoadingState';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {ProviderConsoleService} from '@/shared/services';
import SingleResultContent from './SingleResultContent';
import type {ProviderPatientDataResponse} from '@/modules/provider-console/services/providerConsoleService';
import type {Test} from '@/shared/types/api';

const getOrderDateMs = (order: Test): number => {
    const statusMs = order.statusDate ? new Date(order.statusDate).getTime() : 0;
    const createdMs = order.createdDate ? new Date(order.createdDate).getTime() : 0;
    const amendedMs = order.amendedDate ? new Date(order.amendedDate).getTime() : 0;
    const best = Math.max(statusMs, createdMs, amendedMs);
    return Number.isFinite(best) ? best : 0;
};

/**
 * Build a synthetic order that merges results from ALL orders.
 * For each biomarker (by test_name) we keep only the most recent result.
 * The shell (dates, status, patient info) comes from the newest order.
 */
const buildMergedOrder = (orders: Test[]): Test | null => {
    if (!orders || orders.length === 0) return null;

    // Sort orders newest-first so the first match per test_name wins.
    const sorted = [...orders].sort((a, b) => getOrderDateMs(b) - getOrderDateMs(a));
    const shell = sorted[0]; // newest order provides the outer metadata

    const seen = new Set<string>();
    const mergedResults: Test['results'] = [];

    for (const order of sorted) {
        const orderDate = order.statusDate || order.amendedDate || order.createdDate;
        for (const result of order.results ?? []) {
            const key = (result.test_name ?? '').toLowerCase().trim();
            if (!key || seen.has(key)) continue;
            seen.add(key);
            mergedResults.push({
                ...result,
                statusDate: result.statusDate || orderDate,
            });
        }
    }

    return {
        ...shell,
        results: mergedResults,
    };
};

const normalizePatientGender = (rawGender: string | null | undefined): 'male' | 'female' | undefined => {
    if (!rawGender) return undefined;
    const normalized = rawGender.toLowerCase();
    if (normalized === 'f' || normalized === 'female') return 'female';
    if (normalized === 'm' || normalized === 'male') return 'male';
    return undefined;
};

const ProviderPatientResults: React.FC = () => {
    const {userId} = useParams<{userId: string}>();
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = (location.state as {returnTo?: string} | null)?.returnTo?.toString() || '/provider';
    const {user, token} = useAuth();

    const [patientData, setPatientData] = useState<ProviderPatientDataResponse | null>(null);
    const [patientDataLoading, setPatientDataLoading] = useState(false);
    const [patientDataError, setPatientDataError] = useState<string | null>(null);

    const numericUserId = Number(userId);
    const invalidUserId = !Number.isFinite(numericUserId) || numericUserId <= 0;

    useEffect(() => {
        if (!user?.isLoggedIn) {
            return;
        }
        if (invalidUserId) {
            return;
        }

        let canceled = false;
        const run = async () => {
            setPatientDataError(null);
            setPatientDataLoading(true);
            try {
                const data = await ProviderConsoleService.getPatientData(numericUserId);
                if (!canceled) {
                    setPatientData(data);
                }
            } catch (error: unknown) {
                if (canceled) return;
                // A provider can only open a patient they have an active relationship
                // with — the same rule that decides who appears in their patient list.
                // A 404/403 here means this patient simply isn't on their roster, so
                // show that plainly instead of the raw "Resource not found", and
                // without disclosing which office the patient actually belongs to.
                const status = (error as {status?: number} | null)?.status;
                const message =
                    status === 404 || status === 403
                        ? "You don't have access to this patient. They aren't in your patient list."
                        : error instanceof Error
                          ? error.message
                          : 'Unable to load this patient. You may not have access.';
                setPatientData(null);
                setPatientDataError(message);
            } finally {
                if (!canceled) {
                    setPatientDataLoading(false);
                }
            }
        };

        run();
        return () => {
            canceled = true;
        };
    }, [invalidUserId, numericUserId, user?.isLoggedIn]);

    const orders = useMemo(() => patientData?.orders ?? [], [patientData?.orders]);
    const selectedOrder = useMemo(() => buildMergedOrder(orders), [orders]);
    const needsFetch = !patientData && !patientDataError && !invalidUserId;

    // Declared before any early returns so hook order stays stable across
    // renders — moving it below the guards triggers React's "Rendered more
    // hooks than during the previous render" error.
    const handleRefetch = useCallback(() => {
        if (invalidUserId || !user?.isLoggedIn) return;
        void ProviderConsoleService.getPatientData(numericUserId).then(setPatientData);
    }, [invalidUserId, numericUserId, user?.isLoggedIn]);

    if (invalidUserId) {
        return (
            <DashboardLayout>
                <PageState
                    tone="error"
                    title="Invalid Patient"
                    description="The patient identifier in the URL is invalid."
                    primaryAction={{label: 'Back to Provider Console', onClick: () => navigate(returnTo)}}
                />
            </DashboardLayout>
        );
    }

    if (patientDataLoading || needsFetch) {
        return (
            <DashboardLayout>
                <ResultLoadingState />
            </DashboardLayout>
        );
    }

    if (patientDataError) {
        return (
            <DashboardLayout>
                <PageState
                    tone="error"
                    title="Unable to Load Patient"
                    description={patientDataError}
                    primaryAction={{label: 'Back to Provider Console', onClick: () => navigate(returnTo)}}
                />
            </DashboardLayout>
        );
    }

    if (!selectedOrder) {
        return (
            <DashboardLayout>
                <PageState
                    title="Result Not Found"
                    description="The requested test results could not be found."
                    primaryAction={{label: 'Back to Provider Console', onClick: () => navigate(returnTo)}}
                />
            </DashboardLayout>
        );
    }

    const downloadToken = token || user?.token;
    const patientGender = normalizePatientGender(patientData?.patient?.gender);

    return (
        <SingleResultContent
            result={selectedOrder}
            realResult={selectedOrder}
            token={downloadToken}
            backTo={returnTo}
            backLabel="Back to Provider Console"
            allPatientData={orders}
            patientGender={patientGender}
            patientUserId={numericUserId}
            onOrderRefetch={handleRefetch}
        />
    );
};

export default ProviderPatientResults;
